import { useCallback, useEffect, useRef, useState } from "react";
import { LayoutGrid, List, MoreVertical, Plus, Settings } from "lucide-react";
import AddColumnModal from "../OptimizedKanban/AddStatusColumn";
import { updateIsOpenAddLead } from "../../store/slices/commonSlice";
import { RootDispatch, RootState } from "../../store";
import { useDispatch, useSelector } from "react-redux";
import { ButtonType } from "../Assets/Button";
import Button from "../Assets/Button";
import { setFilters } from "../../store/slices/kanbanFiltersSlice";
import SearchInput from "../Search/SearchInput";
import { getTaskNotifications } from "../../store/slices/TaskSlice";
import { getCustomerNotificationCount } from "../../store/slices/customerSlice";
import { NavLink } from "react-router-dom";
import customerNotificationImg from "../../assets/images/account-notification-icon.png";

interface NavbarProps {
  onSearchEnter: (q: string) => void;
  isModalOpen: boolean;
  setIsModalOpen: (isOpen: boolean) => void;
  onViewChange: (view: "kanban" | "list") => void;
  filters?: {
    assignee?: string;
    sourceType?: string;
    date?: string;
    state?: string;
    district?: string;
    city?: string;
    pincode?: string;
    searchQuery?: string;
  };
}

export default function Navbar({
  setIsModalOpen,
  onViewChange,
  onSearchEnter,
  filters = {},
}: NavbarProps) {
  const dispatch = useDispatch<RootDispatch>();

  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [view, setView] = useState<"kanban" | "list">("kanban");
  const [, setColumns] = useState<{ status: string }[]>([]);

  const dropdownRef = useRef<HTMLDivElement>(null);

  const notifications = useSelector(
    (state: RootState) => state.task.notifications,
  );
  const customerNotificationCount = useSelector(
    (state: RootState) => state.customer.notificationCount,
  );
  const navConfig = useSelector((state: RootState) => state.routesSlice.nav);
  useEffect(() => {
    dispatch(getTaskNotifications());
    dispatch(getCustomerNotificationCount());
  }, [dispatch]);

  const handleViewChange = useCallback(
    (newView: "kanban" | "list") => {
      setView(newView);
      onViewChange(newView);
    },
    [onViewChange],
  );
  function openAddLead() {
    dispatch(updateIsOpenAddLead(true));
  }

  return (
    <div>
      <nav className="flex flex-wrap items-center justify-between gap-2 p-2 shadow-md relative">
        <div className="flex items-center gap-4 w-full md:w-auto flex-1">
          <Button
            type={ButtonType.Submit}
            onClick={openAddLead}
            className="text-xs bg-[#0077C0] text-white font-bold w-full md:w-auto"
          >
            New
          </Button>

          <div className="hidden md:flex flex-1 px-2">
            <label htmlFor="search" className="sr-only">
              Search customers...
            </label>
            <SearchInput
              placeholder="Search customers..."
              className="w-full px-4 py-2 rounded-lg text-black border focus:outline-none"
              onSearch={(term) => {
                dispatch(setFilters({ searchQuery: term || "" }));
                onSearchEnter(term);
              }}
            />
          </div>
        </div>

        <div className="flex items-center gap-2 w-full md:w-auto">
          <div className="w-full md:hidden">
            <label htmlFor="search-mobile" className="sr-only">
              Search customers...
            </label>
            <SearchInput
              placeholder="Search customers..."
              className="w-full px-4 py-2 rounded-lg text-black border focus:outline-none"
              onSearch={(term) => {
                dispatch(setFilters({ searchQuery: term || "" }));
                onSearchEnter(term);
              }}
            />
          </div>

          <div className="hidden md:flex items-center gap-2 ml-3">
            {navConfig?.notifications && (
              <NavLink
                to="/notifications/"
                className={({ isActive }) =>
                  `relative p-1.5 lg:p-2 rounded-lg hover:bg-gray-100 transition-colors ${
                    isActive ? "bg-gray-200 text-blue-600" : "hover:bg-gray-100"
                  }`
                }
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="w-4 h-4 lg:w-5 lg:h-5 text-yellow-500"
                >
                  <path d="M18 8a6 6 0 0 0-12 0c0 7-3 9-3 9h18s-3-2-3-9"></path>
                  <path d="M13.73 21a2 2 0 0 1-3.46 0"></path>
                </svg>
                {notifications! > 0 && (
                  <span
                    className={`absolute -top-1 ${
                      notifications! < 10
                        ? "-right-1 h-5 w-5"
                        : notifications! > 10 && notifications! < 100
                          ? "-right-2"
                          : "-right-3"
                    } bg-red-500 rounded-full text-white text-[10px] font-semibold p-1 flex items-center justify-center`}
                  >
                    {notifications}
                  </span>
                )}
              </NavLink>
            )}

            {navConfig?.customerNotifications && (
              <NavLink
                to="/customer-notifications/"
                className={({ isActive }) =>
                  `relative p-1.5 lg:p-2 rounded-lg hover:bg-gray-100 transition-colors ${
                    isActive ? "bg-gray-200 text-blue-600" : "hover:bg-gray-100"
                  }`
                }
              >
                <img
                  src={customerNotificationImg}
                  alt="Customer Notifications"
                  className="h-6 w-6 object-contain"
                />
                {customerNotificationCount! > 0 && (
                  <span
                    className={`absolute -top-0.5  ${
                      customerNotificationCount! < 10
                        ? "-right-1 h-5 w-5"
                        : customerNotificationCount! > 10 &&
                            customerNotificationCount! < 100
                          ? "-right-1"
                          : "-right-2"
                    } bg-red-500 rounded-full text-white text-[10px] font-semibold p-1 flex items-center justify-center`}
                  >
                    {customerNotificationCount}
                  </span>
                )}
              </NavLink>
            )}
          </div>

          <button
            onClick={() => handleViewChange("kanban")}
            className={`p-2 rounded-full ${
              view === "kanban" ? "bg-[#0077C0] color text-white" : ""
            }`}
            aria-label="kanban view"
            data-testid="kanban-view"
          >
            <LayoutGrid size={20} />
          </button>
          <button
            onClick={() => handleViewChange("list")}
            className={`p-2 rounded-full ${
              view === "list" ? "bg-[#0077C0] color text-white" : ""
            }`}
            aria-label="List View"
          >
            <List size={20} />
          </button>

          <div className="hidden md:flex items-center gap-2">
            <button
              onClick={() => setModalOpen(true)}
              className="p-2 rounded-full hover:bg-[#0077C0] hover:text-white"
              aria-label="plus"
            >
              <Plus size={20} />
            </button>
            <button
              onClick={() => setIsModalOpen(true)}
              className="p-2 rounded-full hover:bg-[#0077C0] hover:text-white"
              aria-label="settings"
            >
              <Settings size={20} />
            </button>
          </div>

          <div className="flex md:hidden">
            <button
              onClick={(e) => {
                e.stopPropagation();
                setDropdownOpen((prev) => !prev);
              }}
              className="p-2 rounded-full hover:bg-[#0077C0] hover:text-white"
              aria-label="more options"
            >
              <MoreVertical />
            </button>
          </div>
        </div>

        {dropdownOpen && (
          <div
            ref={dropdownRef}
            className="absolute top-full right-0 w-56 bg-white border shadow-lg rounded-lg z-10 overflow-hidden"
          >
            <div className="py-1">
              {navConfig?.notifications && (
                <NavLink
                  to="/notifications/"
                  onClick={() => setDropdownOpen(false)}
                  className="flex items-center justify-between px-4 py-2 text-left hover:bg-gray-100 text-gray-800"
                >
                  <div className="flex items-center gap-2">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      className="w-5 h-5 text-yellow-400"
                    >
                      <path d="M18 8a6 6 0 0 0-12 0c0 7-3 9-3 9h18s-3-2-3-9"></path>
                      <path d="M13.73 21a2 2 0 0 1-3.46 0"></path>
                    </svg>
                    <span>Notifications</span>
                  </div>
                  {notifications! > 0 && (
                    <span className="bg-red-500 rounded-full text-white text-xs font-semibold px-2 py-0.5">
                      {notifications}
                    </span>
                  )}
                </NavLink>
              )}

              {navConfig?.customerNotifications && (
                <NavLink
                  to="/customer-notifications/"
                  onClick={() => setDropdownOpen(false)}
                  className="flex items-center justify-between px-4 py-2 text-left hover:bg-gray-100 text-gray-800"
                >
                  <div className="flex items-center gap-2">
                    <img
                      src={customerNotificationImg}
                      alt=""
                      className="h-5 w-5 object-contain"
                    />
                    <span>Customer Notifications</span>
                  </div>
                  {customerNotificationCount! > 0 && (
                    <span className="bg-red-500 rounded-full text-white text-xs font-semibold px-2 py-0.5">
                      {customerNotificationCount}
                    </span>
                  )}
                </NavLink>
              )}
            </div>
            <div className="border-t border-gray-700 py-1">
              <button
                onClick={() => {
                  setModalOpen(true);
                  setDropdownOpen(false);
                }}
                className="block w-full text-left px-4 py-2 hover:bg-gray-100 rounded text-gray-800"
              >
                Add Column
              </button>
              <button
                onClick={() => {
                  setIsModalOpen(true);
                  setDropdownOpen(false);
                }}
                className="block w-full text-left px-4 py-2 hover:bg-gray-100 rounded text-gray-800"
              >
                Status
              </button>
            </div>
          </div>
        )}
      </nav>

      <AddColumnModal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        onColumnAdded={(newColumn) =>
          setColumns((prevColumns) => [...prevColumns, newColumn])
        }
      />
    </div>
  );
}
